import { useState } from "react";
import { toast } from "sonner";
import {
    User,
    MapPin,
    Phone,
    Mail,
    Globe,
    Send,
    Loader2,
    MessageSquare,
} from "lucide-react";

const INFO = [
    { icon: User, label: "Proprietor", value: "Kisan Gaikwad" },
    { icon: MapPin, label: "Address", value: "Shop No. B3, Moksh Plaza, Behind State Bank, Malegaon Maharashtra 423203" },
    { icon: Phone, label: "Mobile", value: "+91 9850881628", href: "tel:+919850881628" },
    { icon: Mail, label: "Email", value: "support@smartsolutions.net.in", href: "mailto:support@smartsolutions.net.in" },
    { icon: Globe, label: "Website", value: "www.smartsolutions.net.in", href: "https://www.smartsolutions.net.in" },
];

const ENQUIRY_ENDPOINT = "/backend/api/send-enquiry.php";

export default function ContactPage() {
    const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "", website: "" });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const onChange = (key) => (event) => {
        const value = key === "mobile"
            ? event.target.value.replace(/\D/g, "").slice(0, 10)
            : event.target.value;
        setForm((s) => ({ ...s, [key]: value }));
        if (errors[key]) setErrors((s) => ({ ...s, [key]: "" }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your name.";
        if (!/^[0-9]{10}$/.test(form.mobile)) e.mobile = "Enter a valid 10-digit mobile.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
        if (!form.message.trim() || form.message.trim().length < 10)
            e.message = "Please share a bit more (min 10 chars).";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;
        setLoading(true);

        try {
            const response = await fetch(ENQUIRY_ENDPOINT, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(form),
            });

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch {
                throw new Error("The server returned an invalid response.");
            }

            if (!response.ok || !result.success) {
                throw new Error(result.message || "Unable to send your enquiry.");
            }

            toast.success("Enquiry sent successfully. We'll contact you shortly.");
            setForm({ name: "", mobile: "", email: "", message: "", website: "" });
            setErrors({});
        } catch (error) {
            toast.error(error.message || "Unable to send your enquiry. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
            data-testid="contact-page"
        >
            <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass-card text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                    Contact Us
                </span>
                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                    Let&apos;s get your <span className="text-gradient-brand">work done.</span>
                </h1>
                <p className="mt-4 text-base sm:text-lg text-muted-foreground">
                    Reach out for enquiries, quotes or partnership. Typical response within a few hours.
                </p>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Info + map */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="glass-card rounded-3xl p-6 lg:p-7" data-testid="contact-info-card">
                        <h2 className="text-lg font-display font-semibold tracking-tight">
                            Contact Details
                        </h2>
                        <div className="mt-5 space-y-4">
                            {INFO.map((it) => {
                                const Icon = it.icon;
                                const body = (
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#3BC7FF] to-[#7B61FF] p-[1.5px] shrink-0">
                                            <div className="h-full w-full rounded-[10px] bg-background/80 backdrop-blur flex items-center justify-center">
                                                <Icon className="h-4 w-4" strokeWidth={1.8} />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                                {it.label}
                                            </div>
                                            <div className="text-sm mt-0.5">{it.value}</div>
                                        </div>
                                    </div>
                                );
                                return it.href ? (
                                    <a
                                        key={it.label}
                                        href={it.href}
                                        target={it.href.startsWith("http") ? "_blank" : undefined}
                                        rel="noreferrer"
                                        data-testid={`contact-info-${it.label.toLowerCase()}`}
                                        className="block hover:opacity-90 transition"
                                    >
                                        {body}
                                    </a>
                                ) : (
                                    <div key={it.label} data-testid={`contact-info-${it.label.toLowerCase()}`}>
                                        {body}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div
                        className="glass-card rounded-3xl overflow-hidden relative"
                        data-testid="contact-map"
                    >
                        <input
                            type="text"
                            name="website"
                            value={form.website}
                            onChange={onChange("website")}
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            className="hidden"
                        />
                        <div className="aspect-[16/10] w-full relative">
                            {/* Stylised map placeholder */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#3BC7FF]/15 via-transparent to-[#7B61FF]/20" />
                            <svg
                                viewBox="0 0 400 250"
                                className="absolute inset-0 h-full w-full opacity-70"
                                aria-hidden="true"
                            >
                                <defs>
                                    <linearGradient id="mapline" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#3BC7FF" />
                                        <stop offset="100%" stopColor="#7B61FF" />
                                    </linearGradient>
                                </defs>
                                <g stroke="url(#mapline)" strokeWidth="1.2" fill="none" opacity="0.55">
                                    <path d="M0 60 Q120 20 260 90 T400 80" />
                                    <path d="M0 130 Q140 90 240 160 T400 150" />
                                    <path d="M0 200 Q160 170 260 220 T400 210" />
                                    <path d="M60 0 Q100 100 60 250" />
                                    <path d="M200 0 Q240 120 200 250" />
                                    <path d="M320 0 Q360 140 320 250" />
                                </g>
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
                                <div className="h-12 w-12 rounded-full btn-gradient flex items-center justify-center text-white shadow-lg animate-pulse">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div className="font-display font-semibold text-sm">
                                    Malegaon, Maharashtra
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Map preview — live embed available in production
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="lg:col-span-3">
                    <form
                        onSubmit={handleSubmit}
                        className="glass-card rounded-3xl p-6 lg:p-8"
                        data-testid="contact-form"
                        noValidate
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl btn-gradient flex items-center justify-center text-white">
                                <MessageSquare className="h-4 w-4" />
                            </div>
                            <h2 className="text-lg font-display font-semibold tracking-tight">
                                Send us a message
                            </h2>
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field
                                id="name"
                                label="Full Name"
                                placeholder="e.g. Kisan Gaikwad"
                                value={form.name}
                                onChange={onChange("name")}
                                error={errors.name}
                                testId="contact-name-input"
                            />
                            <Field
                                id="mobile"
                                label="Mobile"
                                placeholder="10-digit mobile"
                                value={form.mobile}
                                onChange={onChange("mobile")}
                                error={errors.mobile}
                                mono
                                inputMode="numeric"
                                testId="contact-mobile-input"
                            />
                            <div className="sm:col-span-2">
                                <Field
                                    id="email"
                                    label="Email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    error={errors.email}
                                    testId="contact-email-input"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="message"
                                    className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground"
                                >
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange("message")}
                                    placeholder="Tell us how we can help…"
                                    data-testid="contact-message-input"
                                    className="mt-2 w-full rounded-xl bg-background/60 border border-border focus:border-primary focus:ring-2 focus:ring-primary/25 outline-none px-4 py-3 text-sm transition resize-none"
                                />
                                {errors.message && (
                                    <p
                                        className="mt-1.5 text-xs text-destructive"
                                        data-testid="contact-message-error"
                                    >
                                        {errors.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                            <p className="text-xs text-muted-foreground">
                                We usually reply within a few working hours.
                            </p>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit-btn"
                                className="inline-flex items-center gap-2 h-11 px-6 rounded-full btn-gradient ripple text-white text-sm font-semibold disabled:opacity-70"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4" /> Send message
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

const Field = ({ id, label, error, mono, testId, ...props }) => (
    <div>
        <label
            htmlFor={id}
            className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground"
        >
            {label}
        </label>
        <input
            id={id}
            data-testid={testId}
            {...props}
            className={`mt-2 w-full h-11 px-4 rounded-xl bg-background/60 border border-border focus:border-primary focus:ring-2 focus:ring-primary/25 outline-none text-sm transition ${mono ? "font-mono tracking-wider" : ""}`}
        />
        {error && (
            <p className="mt-1.5 text-xs text-destructive" data-testid={`${testId}-error`}>
                {error}
            </p>
        )}
    </div>
);
